import React from 'react'
import { DataCard } from '@/components/DataCard/DataCard'
import { FormField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { RateMarkupDto } from 'api/Exchange-Rates/RateMarkupDto'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'
import { WalletTypeDto } from 'api/Wallet/Types/WalletTypeDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues, IntegerFrom1To8Pattern } from 'utils/formValidation'

export interface AddEditRateMarkupFormValues {
  id?: string
  markup: number
  precision: number
  debitWalletTypeId: number
  debitCurrencyId: string
  creditWalletTypeId: number
  creditCurrencyId: string
}

const AddEditRateMarkupFormUI: React.FC<FormikProps<AddEditRateMarkupFormValues> & OuterProps> = (
  props
) => {
  const { handleSubmit, setSubmitting, isSubmitting, errors, rateMarkup, onClose } = props
  const walletCurrencyOptions = props.walletCurrencies.map((curr) => ({
    id: curr.id,
    name: curr.id,
  }))

  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle>{rateMarkup ? 'Edit Rate Markup' : 'Add Rate Markup'}</DialogTitle>
        </DialogHeader>
        <Form>
          <div className='grid gap-4 md:grid-cols-2'>
            <DataCard title='Debit'>
              <FormSelectField
                name='debitWalletTypeId'
                label='Wallet Type'
                options={props.walletType}
                error={errors.debitWalletTypeId}
                onChange={(value) => props.setFieldValue('debitWalletTypeId', Number(value))}
              />
              <FormSelectField
                name='debitCurrencyId'
                label='Wallet Currency'
                options={walletCurrencyOptions}
                error={errors.debitCurrencyId}
                onChange={(value) => props.setFieldValue('debitCurrencyId', value)}
              />
            </DataCard>

            <DataCard title='Credit'>
              <FormSelectField
                name='creditWalletTypeId'
                label='Wallet Type'
                error={errors.creditWalletTypeId}
                options={props.walletType}
                onChange={(value) => props.setFieldValue('creditWalletTypeId', Number(value))}
              />
              <FormSelectField
                name='creditCurrencyId'
                label='Wallet Currency'
                error={errors.creditCurrencyId}
                options={walletCurrencyOptions}
                onChange={(value) => props.setFieldValue('creditCurrencyId', value)}
              />
            </DataCard>

            <DataCard title='Markup' columns={2} className='md:col-span-2'>
              <FormField
                name='markup'
                label='Amount'
                type='number'
                dataTest='amount-field'
                error={errors.markup}
              />
              <FormField
                name='precision'
                label='Precision'
                type='number'
                dataTest='precision-field'
                error={errors.precision}
              />
            </DataCard>
          </div>
          <DialogFooter>
            <Button disabled={isSubmitting} onClick={onClose} type='button' variant='outline'>
              Cancel
            </Button>
            <Button disabled={isSubmitting} onClick={handleSubmitForm} type='button'>
              Save
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  rateMarkup?: RateMarkupDto
  permissions: PermissionDto[]
  walletType: WalletTypeDto[]
  walletCurrencies: WalletCurrencyDto[]

  onSubmit(values: FormSubmitValues<AddEditRateMarkupFormValues>): Promise<void>
}

export const AddEditRateMarkupForm = withFormik<OuterProps, AddEditRateMarkupFormValues>({
  mapPropsToValues: ({ rateMarkup }) => {
    return {
      id: rateMarkup?.id || '',
      markup: rateMarkup ? rateMarkup.markup : 0.00001,
      precision: rateMarkup?.precision || 6,
      creditCurrencyId: rateMarkup?.creditCurrency?.id || '',
      creditWalletTypeId: rateMarkup?.creditWalletType?.id || 0,
      debitCurrencyId: rateMarkup?.debitCurrency?.id || '',
      debitWalletTypeId: rateMarkup?.debitWalletType?.id || 0,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddEditRateMarkupFormValues> = {}
    if (!values.debitWalletTypeId) {
      errors.debitWalletTypeId = 'required'
    }
    if (!values.debitCurrencyId) {
      errors.debitCurrencyId = 'required'
    }
    if (!values.creditWalletTypeId) {
      errors.creditWalletTypeId = 'required'
    }
    if (!values.creditCurrencyId) {
      errors.creditCurrencyId = 'required'
    }
    if (!values.precision) {
      errors.precision = 'required'
    } else if (!IntegerFrom1To8Pattern.test(values.precision.toString())) {
      errors.precision = 'Must be value from 1 to 8'
    }
    return errors
  },
  enableReinitialize: true,
})(AddEditRateMarkupFormUI)
